import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  Copy,
  CreditCard,
  ShieldCheck,
  ShoppingCart,
  Ticket,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { authService, paymentsService } from '../services/api';

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const { items, removeItem, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupRef, setTopupRef] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupFile, setTopupFile] = useState<File | null>(null);
  const [topupMethod, setTopupMethod] = useState<'CARD' | 'SHEBA' | 'ACCOUNT'>('CARD');
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [, setTopupRequests] = useState<any[]>([]);
  const [cartOrderMap, setCartOrderMap] = useState<Record<string, string>>({});
  const [checkoutCouponCode, setCheckoutCouponCode] = useState('');
  type CartCouponPreview = Awaited<ReturnType<(typeof paymentsService)['previewCartCoupon']>>;
  const [cartCouponPreview, setCartCouponPreview] = useState<CartCouponPreview | null>(null);
  const [cartPreviewLoading, setCartPreviewLoading] = useState(false);
  const [activationNotif, setActivationNotif] = useState<{
    uid: string;
    courseId: string;
    title: string;
    subtitle?: string;
    thumbnail?: string;
    price?: number;
  } | null>(null);

  const totals = useMemo(() => {
    const list = items.reduce((sum, item) => sum + Number(item.listPrice || 0), 0);
    const pay = items.reduce((sum, item) => sum + Number(item.price || 0), 0);
    return { list, pay, saved: Math.max(0, list - pay) };
  }, [items]);

  const couponPreviewApplicable =
    cartCouponPreview?.kind === 'applied' || cartCouponPreview?.kind === 'partial';
  const totalPayAfterCoupon =
    couponPreviewApplicable && cartCouponPreview
      ? cartCouponPreview.totalFinalPayableToman
      : totals.pay;
  const extraCouponDiscount =
    couponPreviewApplicable && cartCouponPreview ? cartCouponPreview.totalDiscountToman : 0;

  useEffect(() => {
    setCartCouponPreview(null);
  }, [items, checkoutCouponCode]);

  const handlePreviewCartCoupon = async () => {
    setError('');
    if (!user) {
      setError('برای بررسی کد تخفیف وارد حساب شوید.');
      return;
    }
    if (items.length === 0 || !checkoutCouponCode.trim()) {
      setError('سبد یا کد تخفیف را تکمیل کنید.');
      return;
    }
    setCartPreviewLoading(true);
    try {
      const res = await paymentsService.previewCartCoupon(
        checkoutCouponCode.trim().toUpperCase(),
        items.map((i) => i.id),
      );
      setCartCouponPreview(res);
      if (res.kind === 'none' && res.rejectionReason) {
        setError(res.rejectionReason);
      }
    } catch {
      setError('خطا در بررسی کوپن. دوباره تلاش کنید.');
      setCartCouponPreview(null);
    } finally {
      setCartPreviewLoading(false);
    }
  };

  const toFaDigits = (value: number | string) => String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
  const normalizeDigits = (value: string) =>
    value
      .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
      .replace(/[^\d]/g, '');
  const formatThousands = (value: string) => {
    const digits = normalizeDigits(value);
    if (!digits) return '';
    return Number(digits).toLocaleString('en-US');
  };
  const threeDigitsToWords = (n: number): string => {
    const ones = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
    const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
    const hundreds = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
    const parts: string[] = [];
    const h = Math.floor(n / 100);
    const rem = n % 100;
    if (h) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const o = rem % 10;
      if (t) parts.push(tens[t]);
      if (o) parts.push(ones[o]);
    }
    return parts.join(' و ');
  };
  const numberToPersianWords = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return '';
    if (value === 0) return 'صفر';
    const units = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];
    let n = Math.floor(value);
    let unitIndex = 0;
    const chunks: string[] = [];
    while (n > 0 && unitIndex < units.length) {
      const chunk = n % 1000;
      if (chunk > 0) {
        const chunkWords = threeDigitsToWords(chunk);
        chunks.unshift(chunkWords + (units[unitIndex] ? ` ${units[unitIndex]}` : ''));
      }
      n = Math.floor(n / 1000);
      unitIndex += 1;
    }
    return chunks.join(' و ');
  };
  const topupAmountNumber = Number(normalizeDigits(topupAmount || '')) || 0;
  const topupAmountInWords = topupAmountNumber > 0 ? `${numberToPersianWords(topupAmountNumber)} تومان` : '';

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!user) return;
      try {
        const wallet = await paymentsService.getWalletBalance();
        if (!alive) return;
        setWalletBalance(Number(wallet?.balance || 0));
      } catch {
        if (!alive) return;
        setWalletBalance(Number(user?.wallet?.balance || 0));
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!user) return;
      try {
        const rows = await paymentsService.getMyWalletTopupRequests();
        if (!alive) return;
        setTopupRequests(Array.isArray(rows) ? rows : []);
      } catch {
        if (!alive) return;
        setTopupRequests([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, isTopupModalOpen, message]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!user || items.length === 0) return;
      for (const item of items) {
        if (cartOrderMap[item.id]) continue;
        try {
          const order = await paymentsService.createOrGetCartOrder(item.id);
          if (!alive) return;
          if (order?.id) {
            setCartOrderMap((prev) => ({ ...prev, [item.id]: order.id }));
          }
        } catch (err: any) {
          const msg = String(err?.response?.data?.message || '');
          // If course is already enrolled, remove stale cart item to prevent repeated backend errors.
          if (msg.includes('قبلا در این دوره ثبت‌نام شده')) {
            removeItem(item.id);
            setCartOrderMap((prev) => {
              const next = { ...prev };
              delete next[item.id];
              return next;
            });
            continue;
          }
          // Keep UX smooth if order reservation fails for one item.
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, items, cartOrderMap, removeItem]);

  const handleCheckout = async () => {
    if (!user) {
      setError('برای پرداخت ابتدا وارد حساب کاربری شوید یا ثبت نام کنید.');
      return;
    }
    if (items.length === 0) return;

    setLoading(true);
    setMessage('');
    setError('');

    let successCount = 0;
    const failedTitles: string[] = [];
    let couponErrorShown = false;

    const couponOpts = checkoutCouponCode.trim()
      ? {
          couponCode: checkoutCouponCode.trim().toUpperCase(),
          couponCartCourseIds: items.map((i) => i.id),
        }
      : undefined;

    for (const item of items) {
      try {
        const result = await paymentsService.initiateCoursePayment(item.id, couponOpts);
        if (result?.enrolled) {
          successCount += 1;
          removeItem(item.id);
          setCartOrderMap((prev) => {
            const copy = { ...prev };
            delete copy[item.id];
            return copy;
          });
          const uid = `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          setActivationNotif({
            uid,
            courseId: item.id,
            title: item.title,
            subtitle: item.subtitle,
            thumbnail: item.thumbnail || '/assets/homeV2/01.jpg',
            price: item.price,
          });
          window.setTimeout(() => {
            setActivationNotif((prev) => (prev?.uid === uid ? null : prev));
          }, 6500);
        } else {
          failedTitles.push(item.title);
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message;
        if (typeof msg === 'string' && couponOpts?.couponCode && !couponErrorShown) {
          couponErrorShown = true;
          setError(msg);
        }
        failedTitles.push(item.title);
      }
    }

    try {
      const freshUser = await authService.getProfile();
      const token = localStorage.getItem('token');
      if (token) {
        setSession({ token, user: freshUser });
      }
      setWalletBalance(Number(freshUser?.wallet?.balance || 0));
    } catch {
      // Keep cart flow usable even if profile refresh fails.
    }

    if (successCount > 0) {
      setMessage(`${successCount.toLocaleString('fa-IR')} دوره با موفقیت فعال شد.`);
    }
    if (failedTitles.length > 0) {
      setError(`خرید ${failedTitles.length.toLocaleString('fa-IR')} دوره انجام نشد. موجودی کیف پول کافی نیست.`);
    }
    if (successCount === items.length) {
      clear();
    }
    setLoading(false);
  };

  const [payOnlineLoading, setPayOnlineLoading] = useState(false);

  const handlePayOnline = async () => {
    setError('');
    setMessage('');
    if (!user) {
      setError('برای پرداخت آنلاین ابتدا وارد حساب کاربری شوید یا ثبت نام کنید.');
      return;
    }
    if (items.length === 0) return;

    const shortfallToman = Math.round(totalPayAfterCoupon - walletBalance);
    if (shortfallToman <= 0) return;

    setPayOnlineLoading(true);
    try {
      const couponCode = couponPreviewApplicable ? checkoutCouponCode.trim().toUpperCase() : undefined;
      const data = await paymentsService.chargeWallet(
        shortfallToman * 10,
        'شارژ کیف پول برای تکمیل خرید سبد',
        { courseIds: items.map((i) => i.id), couponCode },
      );
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        setError('خطا در دریافت آدرس درگاه پرداخت.');
        setPayOnlineLoading(false);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'اتصال به درگاه پرداخت ناموفق بود.');
      setPayOnlineLoading(false);
    }
  };

  const handleSubmitTopupRequest = async () => {
    if (!user) {
      setError('برای ثبت درخواست افزایش موجودی ابتدا وارد حساب کاربری شوید یا ثبت نام کنید.');
      setIsTopupModalOpen(false);
      return;
    }
    const amount = Math.round(Number(normalizeDigits(topupAmount || '')) || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('مبلغ درخواست افزایش موجودی معتبر نیست.');
      return;
    }
    if (!topupRef.trim() && !topupFile) {
      setError('شماره پیگیری یا تصویر رسید را وارد کنید.');
      return;
    }

    setTopupSubmitting(true);
    setError('');
    try {
      let receiptImageUrl: string | undefined;
      if (topupFile) {
        const uploaded = await paymentsService.uploadTopupReceipt(topupFile);
        receiptImageUrl = uploaded.url;
      }
      await paymentsService.createWalletTopupRequest({
        amount,
        depositMethod: topupMethod,
        receiptReference: topupRef.trim() || undefined,
        receiptImageUrl,
        note: topupNote.trim() || undefined,
      });
      setMessage('درخواست افزایش موجودی ثبت شد و پس از تایید مالی کیف پول شما شارژ می‌شود.');
      setIsTopupModalOpen(false);
      setTopupAmount('');
      setTopupRef('');
      setTopupNote('');
      setTopupFile(null);
      setTopupMethod('CARD');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ثبت درخواست افزایش موجودی ناموفق بود.');
    } finally {
      setTopupSubmitting(false);
    }
  };

  const handleCopyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage('کپی شد.');
    } catch {
      // noop
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7fc] px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-4 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_12px_44px_-24px_rgba(15,23,42,0.28)] sm:mb-6 sm:rounded-3xl sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-[#3A68C2]">سبد خرید</p>
              <h1 className="mt-1 text-xl font-black text-[#0F172A] sm:text-3xl">تسویه حرفه‌ای با کیف پول</h1>
              <p className="mt-2 text-xs font-medium leading-6 text-slate-600 sm:text-sm sm:leading-7">
                دوره‌ها را بررسی کن، از جزئیات مطمئن شو و در صورت کافی بودن اعتبار، با یک کلیک فعال‌سازی انجام بده.
              </p>
            </div>
            <Link
              to="/courses"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 sm:text-xs"
            >
              بازگشت به دوره‌ها
              <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
              <ShoppingCart className="h-3.5 w-3.5 text-slate-600" />
              {items.length.toLocaleString('fa-IR')} دوره در سبد
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              پرداخت امن با کیف پول
            </span>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_23rem] lg:gap-5">
          <section className="order-2 rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_10px_36px_-20px_rgba(15,23,42,0.25)] sm:rounded-3xl sm:p-5 lg:order-1">
            {items.length === 0 ? (
              <div className="py-14 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                  <ShoppingCart className="h-8 w-8" />
                </div>
                <p className="text-lg font-black text-slate-800">سبد خرید خالی است</p>
                <p className="mt-1 text-sm text-slate-500">از صفحه دوره‌ها، دوره مدنظر خودت را اضافه کن.</p>
                <Link
                  to="/courses"
                  className="mt-5 inline-flex rounded-xl bg-[#3A68C2] px-4 py-2.5 text-sm font-extrabold text-white hover:bg-[#345DAF]"
                >
                  مشاهده دوره‌ها
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <article
                    key={item.id}
                    className="group rounded-2xl border border-slate-200/80 bg-slate-50/70 p-3.5 transition hover:border-[#3A68C2]/30 hover:bg-white sm:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <img
                          src={item.thumbnail || '/assets/homeV2/01.jpg'}
                          alt={item.title}
                          className="h-16 w-16 shrink-0 rounded-xl object-cover ring-1 ring-slate-200 sm:h-24 sm:w-24"
                        />
                        <div className="min-w-0">
                          <p className="line-clamp-2 text-sm font-extrabold leading-6 text-[#0F172A]">{item.title}</p>
                          <p className="mt-1 truncate text-[11px] font-medium text-slate-500">{item.subtitle}</p>
                          {item.listPrice > item.price ? (
                            <span className="mt-2 inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black text-white">
                              {`٪${toFaDigits(Math.max(0, Math.round(((item.listPrice - item.price) / item.listPrice) * 100)))}`}
                            </span>
                          ) : null}
                          <Link
                            to={`/courses/${item.id}`}
                            className="mt-2 inline-flex text-[11px] font-bold text-[#3A68C2] hover:text-[#345DAF]"
                          >
                            مشاهده صفحه دوره
                          </Link>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 transition hover:border-red-200 hover:bg-red-100 hover:text-red-700 sm:hidden"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-3 flex items-end justify-between border-t border-slate-200/80 pt-2.5 sm:mt-4 sm:pt-3">
                      <div className="text-right">
                        <p className="text-xs font-medium text-slate-500">قیمت نهایی</p>
                        <p className="text-base font-black text-[#1C3B9B] sm:text-lg">{Number(item.price).toLocaleString('fa-IR')} تومان</p>
                        {item.listPrice > item.price ? (
                          <p className="text-[11px] text-slate-400 line-through">{Number(item.listPrice).toLocaleString('fa-IR')} تومان</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="hidden items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-red-600 transition hover:bg-red-50 hover:text-red-700 sm:inline-flex"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        حذف
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <aside className="order-1 h-fit lg:order-2 lg:sticky lg:top-24">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_10px_36px_-20px_rgba(15,23,42,0.25)] sm:rounded-3xl sm:p-5 space-y-4">
              <div>
                <h2 className="text-base font-black text-[#0F172A]">کارت تسویه و پرداخت</h2>
              </div>

              <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50 p-3.5 shadow-inner shadow-slate-100/90 space-y-3">
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5">
                  <p className="text-[11px] font-semibold text-blue-700">اعتبار کیف پول</p>
                  <p className="mt-1 text-base font-black text-blue-900">{walletBalance.toLocaleString('fa-IR')} تومان</p>
                </div>

                {items.length > 0 ? (
                  <div className="relative overflow-hidden rounded-2xl border border-[#c7d7f7]/70 bg-gradient-to-br from-[#f5f8ff] via-white to-[#f3eefc]/90 p-[1px] shadow-[0_14px_40px_-28px_rgba(28,59,155,0.45)]">
                    <div className="rounded-[calc(1rem-1px)] bg-white/80 px-3.5 py-3.5 backdrop-blur-[2px] sm:px-4 sm:py-4">
                      <div className="pointer-events-none absolute -left-24 -top-20 h-40 w-40 rounded-full bg-[#3A68C2]/18 blur-3xl" />
                      <div className="pointer-events-none absolute -bottom-24 -right-16 h-36 w-36 rounded-full bg-violet-400/14 blur-3xl" />
                      <div className="relative flex items-center gap-2.5 min-w-0">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C3B9B] to-[#3A68C2] text-white shadow-md shadow-[#1C3B9B]/25 ring-2 ring-white/80">
                          <Ticket className="h-4.5 w-4.5" strokeWidth={2.25} />
                        </span>
                        <label htmlFor="cart-coupon" className="min-w-0 text-[13px] font-black tracking-tight text-[#0F172A]">
                          کد تخفیف سبد
                        </label>
                      </div>
                      <div className="relative mt-2.5 flex gap-2 sm:gap-2.5">
                        <input
                          id="cart-coupon"
                          dir="ltr"
                          autoComplete="off"
                          autoCapitalize="characters"
                          spellCheck={false}
                          value={checkoutCouponCode}
                          onChange={(e) => {
                            setCheckoutCouponCode(e.target.value.toUpperCase());
                            setCartCouponPreview(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handlePreviewCartCoupon();
                            }
                          }}
                          placeholder="SPRING۱۴۰۴"
                          className="min-w-0 flex-1 rounded-2xl border border-slate-200/95 bg-white/95 px-3.5 py-3 text-[13px] font-bold tracking-[0.06em] text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] outline-none ring-2 ring-transparent transition placeholder:text-slate-400 placeholder:tracking-normal placeholder:font-semibold focus:border-[#3A68C2]/80 focus:ring-[#3A68C2]/22"
                        />
                        <button
                          type="button"
                          onClick={() => void handlePreviewCartCoupon()}
                          disabled={cartPreviewLoading || !user}
                          className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-gradient-to-b from-[#3A68C2] to-[#2d54a8] px-4 py-3 text-[12px] font-black text-white shadow-md shadow-[#2d54a8]/30 transition hover:from-[#345DAF] hover:to-[#294a93] hover:shadow-lg disabled:pointer-events-none disabled:opacity-45"
                        >
                          {cartPreviewLoading ? (
                            <BrandSpinnerRing size="sm" inverse />
                          ) : (
                            'اعمال'
                          )}
                        </button>
                      </div>
                      {!user ? (
                        <p className="relative mt-2.5 rounded-xl border border-amber-200/70 bg-amber-50/90 px-2.5 py-1.5 text-[10px] font-black text-amber-900">
                          برای اعمال کد ابتدا وارد حساب شوید.
                        </p>
                      ) : null}
                      {couponPreviewApplicable && cartCouponPreview ? (
                        <div className="relative mt-3 space-y-2 rounded-xl border border-emerald-200/85 bg-gradient-to-b from-emerald-50/95 to-white/90 px-3 py-2.5 text-[11px] font-semibold text-emerald-950 shadow-sm">
                          <p className="inline-flex flex-wrap items-center gap-1.5">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm">
                              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.75} />
                            </span>
                            <span className="font-bold">
                              مجموع تخفیف اعمال‌شده با کوپن:{' '}
                              <span className="font-black tabular-nums">{extraCouponDiscount.toLocaleString('fa-IR')}</span>{' '}
                              تومان
                            </span>
                          </p>
                          {cartCouponPreview.kind === 'partial' ? (
                            <p className="font-bold text-amber-900 leading-relaxed border-t border-emerald-100/80 pt-2">
                              برای برخی آیتم‌های سبد این کد شامل نشد؛ علت هر مورد را پایین ببینید.
                            </p>
                          ) : null}
                          {cartCouponPreview.lines.some((l) => !l.ok) ? (
                            <ul className="max-h-28 list-disc space-y-0.5 overflow-y-auto rounded-lg bg-white/60 py-2 pr-5 text-[10px] leading-relaxed text-slate-800">
                              {cartCouponPreview.lines
                                .filter((l) => !l.ok)
                                .map((l) => (
                                  <li key={l.courseId}>
                                    <span className="font-bold">{l.title}</span>: {l.rejectionReason}
                                  </li>
                                ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2 text-sm pt-1">
              <div className="flex items-center justify-between text-slate-600">
                <span>جمع قبل از تخفیف</span>
                <span className="font-semibold">{totals.list.toLocaleString('fa-IR')} تومان</span>
              </div>
              <div className="flex items-center justify-between text-emerald-700">
                <span>سود شما (تخفیف دوره)</span>
                <span className="font-bold">{totals.saved.toLocaleString('fa-IR')} تومان</span>
              </div>
              {couponPreviewApplicable && extraCouponDiscount > 0 ? (
                <div className="flex items-center justify-between text-[#14532d]">
                  <span>تخفیف کوپن</span>
                  <span className="font-black">-{extraCouponDiscount.toLocaleString('fa-IR')} تومان</span>
                </div>
              ) : null}
              <div className="border-t border-slate-200 pt-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800">مبلغ قابل پرداخت</span>
                  <span className="text-lg font-black text-[#1C3B9B]">
                    {totalPayAfterCoupon.toLocaleString('fa-IR')} تومان
                  </span>
                </div>
                {couponPreviewApplicable && totals.pay !== totalPayAfterCoupon ? (
                  <p className="mt-1 text-[10px] font-medium text-slate-400 line-through text-left" dir="ltr">
                    قبلاً: {totals.pay.toLocaleString('fa-IR')}
                  </p>
                ) : null}
              </div>
            </div>
            {totalPayAfterCoupon > walletBalance ? (
              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="flex items-center justify-between text-[12px] font-bold text-slate-700">
                  <span>موجودی کیف پول کافی نیست</span>
                  <span className="text-slate-500 font-semibold">
                    کسری: {Math.max(0, Math.round(totalPayAfterCoupon - walletBalance)).toLocaleString('fa-IR')} تومان
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handlePayOnline}
                  disabled={payOnlineLoading}
                  className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm shadow-emerald-600/20 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {payOnlineLoading ? (
                    <>
                      <BrandSpinnerRing size="sm" inverse />
                      در حال انتقال به درگاه بانکی...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      پرداخت از طریق درگاه
                    </>
                  )}
                </button>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-emerald-800">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  پرداخت امن با کارت بانکی از طریق درگاه پی‌پینگ، فعال‌سازی آنی دوره‌ها
                </p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => {
                if (!user) {
                  setError('برای ثبت درخواست افزایش موجودی ابتدا وارد حساب کاربری شوید یا ثبت نام کنید.');
                  return;
                }
                setIsTopupModalOpen(true);
              }}
              className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 transition hover:border-[#1C3B9B]/25 hover:text-[#1C3B9B]"
            >
              یا درخواست افزایش موجودی با واریز دستی و تایید مالی
            </button>

            {message ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold leading-7 text-emerald-700">
                {message}
              </div>
            ) : null}
            {error ? (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold leading-7 text-red-700">
                {error}
              </div>
            ) : null}
            {!user ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="rounded-xl bg-[#3A68C2] px-3 py-2 text-xs font-black text-white transition hover:bg-[#345DAF]"
                >
                  ورود به حساب
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-black text-slate-700 transition hover:bg-slate-50"
                >
                  ثبت نام
                </button>
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleCheckout}
              disabled={loading || items.length === 0}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-3 text-sm font-black text-white transition hover:bg-[#345DAF] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <BrandSpinnerRing size="sm" inverse />
                  در حال پردازش خرید...
                </>
              ) : (
                'پرداخت از کیف پول و فعال‌سازی'
              )}
            </button>
              <p className="mt-1 text-center text-[11px] font-medium text-slate-500">
                با پرداخت موفق، دسترسی دوره‌ها فوری فعال می‌شود.
              </p>
            </div>
          </aside>
        </div>

      </div>

      {isTopupModalOpen ? (
        <div
          className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-slate-900/55 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4"
          onClick={() => setIsTopupModalOpen(false)}
        >
          <div
            className="my-auto w-full max-w-2xl max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl sm:rounded-3xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-black text-[#0F172A]">ثبت حرفه‌ای درخواست افزایش موجودی</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">
                  ۱) واریز به حساب مقصد، ۲) ثبت مبلغ و رسید، ۳) تایید مالی و شارژ خودکار کیف پول.
                </p>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                <BadgeCheck className="h-3.5 w-3.5" />
                پاسخگویی مالی
              </span>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setTopupMethod('CARD')}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  topupMethod === 'CARD' ? 'border-[#1C3B9B] bg-[#1C3B9B]/5 text-[#1C3B9B]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                واریز کارت به کارت
              </button>
              <button
                type="button"
                onClick={() => setTopupMethod('SHEBA')}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  topupMethod === 'SHEBA' ? 'border-[#1C3B9B] bg-[#1C3B9B]/5 text-[#1C3B9B]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                واریز شبا
              </button>
              <button
                type="button"
                onClick={() => setTopupMethod('ACCOUNT')}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  topupMethod === 'ACCOUNT' ? 'border-[#1C3B9B] bg-[#1C3B9B]/5 text-[#1C3B9B]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                واریز حساب
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm text-blue-900">
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-xl bg-white/90 px-3 py-2">
                  <p className="text-[11px] font-bold text-blue-700">شماره کارت</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="font-black">6037-9919-9999-9999</p>
                    <button type="button" onClick={() => handleCopyText('6037991999999999')} className="inline-flex items-center gap-1 text-xs font-bold text-blue-700">
                      <Copy className="h-3.5 w-3.5" /> کپی
                    </button>
                  </div>
                </div>
                <div className="rounded-xl bg-white/90 px-3 py-2">
                  <p className="text-[11px] font-bold text-blue-700">شماره شبا</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="font-black">IR12 0000 0000 0000 0000 0000 00</p>
                    <button type="button" onClick={() => handleCopyText('IR120000000000000000000000')} className="inline-flex items-center gap-1 text-xs font-bold text-blue-700">
                      <Copy className="h-3.5 w-3.5" /> کپی
                    </button>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-xs">
                صاحب حساب: <span className="font-black">مرتضی حقیقی</span>
              </p>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                inputMode="numeric"
                value={formatThousands(topupAmount)}
                onChange={(e) => setTopupAmount(normalizeDigits(e.target.value))}
                placeholder="مبلغ واریزی (تومان)"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold"
              />
              <div className="sm:col-span-2 -mt-1 px-1 text-[11px] font-medium text-slate-500">
                {topupAmountInWords ? `معادل حروفی: ${topupAmountInWords}` : ' '}
              </div>
              <input
                value={topupRef}
                onChange={(e) => setTopupRef(e.target.value)}
                placeholder="شماره پیگیری / مرجع"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold"
              />
              <label className="sm:col-span-2 flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-600 transition hover:border-[#3A68C2]/40 hover:bg-white">
                <span className="inline-flex items-center gap-2 font-semibold">
                  <UploadCloud className="h-4 w-4 text-[#3A68C2]" />
                  {topupFile ? topupFile.name : 'آپلود تصویر رسید (png/jpg/webp)'}
                </span>
                <span className="rounded-lg bg-white px-2 py-1 text-xs font-bold text-slate-700">انتخاب فایل</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setTopupFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <textarea
                value={topupNote}
                onChange={(e) => setTopupNote(e.target.value)}
                placeholder="توضیحات تکمیلی برای تیم مالی (اختیاری)"
                className="sm:col-span-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                rows={3}
              />
            </div>
            <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold leading-6 text-blue-800">
              مبلغی که اینجا ثبت می‌کنید باید دقیقاً با مبلغ واریزی رسید یکی باشد؛ در غیر این صورت درخواست توسط تیم مالی رد می‌شود.
            </div>
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold leading-6 text-amber-800">
              برای تسریع تایید، ترجیحا شماره پیگیری + تصویر رسید را همزمان ثبت کنید.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsTopupModalOpen(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700"
              >
                انصراف
              </button>
              <button
                type="button"
                onClick={handleSubmitTopupRequest}
                disabled={topupSubmitting}
                className="rounded-xl bg-[#1C3B9B] px-5 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {topupSubmitting ? 'در حال ارسال...' : 'ارسال درخواست'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activationNotif ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-3xl border border-white/60 bg-white/95 p-4 shadow-[0_34px_75px_-30px_rgba(15,23,42,0.55)] sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                فعال‌سازی موفق
              </div>
              <button
                type="button"
                onClick={() => setActivationNotif(null)}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
              <div className="flex items-start gap-3">
                <img
                  src={activationNotif.thumbnail || '/assets/homeV2/01.jpg'}
                  alt={activationNotif.title}
                  className="h-20 w-20 shrink-0 rounded-xl object-cover ring-1 ring-slate-200"
                />
                <div className="min-w-0">
                  <p className="text-sm font-black leading-6 text-slate-900">{activationNotif.title}</p>
                  <p className="mt-1 line-clamp-1 text-[11px] font-medium text-slate-500">{activationNotif.subtitle || 'دسترسی دوره فعال شد'}</p>
                  {typeof activationNotif.price === 'number' ? (
                    <p className="mt-2 text-xs font-bold text-[#1C3B9B]">
                      مبلغ پرداختی: {activationNotif.price.toLocaleString('fa-IR')} تومان
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <p className="mt-3 text-center text-xs font-semibold text-slate-600">
              دسترسی شما به این دوره فعال شد و می‌توانید همین حالا یادگیری را شروع کنید.
            </p>

            <button
              type="button"
              onClick={() => navigate(`/courses/${activationNotif.courseId}`)}
              className="mt-4 w-full rounded-xl bg-[#1C3B9B] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#16307F]"
            >
              ورود مستقیم به صفحه دوره
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CartPage;
